import { resolveConfidenceVisitorId } from './confidenceVisitorId'

let initialized = false

/**
 * Starts a Confidence session recording for this browser tab.
 *
 * The recorder captures DOM changes, clicks, inputs, scrolls and route changes
 * and replays them in Confidence next to the flag variants resolved in the same
 * tab, which is what makes a recording answer "where did this variant lose
 * people" rather than just "it lost people".
 *
 * What gets recorded is decided in Confidence rather than here, by a recording
 * policy targeting this client: until a rule in that policy matches, the
 * session is skipped and nothing is captured. The flag client secret is what
 * authenticates the recorder, so there is no separate secret to keep in step
 * per environment.
 *
 * The context passed here is only used to evaluate those rules; it is not
 * shared with the flag SDK, and neither SDK copies it to the other. Passing the
 * same `visitor_id` the flag context uses is what lets a recording rule select
 * the same visitors a flag rule does. It is also fixed at init — later changes
 * to the flag context do not reach the recorder — which is fine here because
 * the visitor id is resolved once per page load and never changes afterwards.
 *
 * The SDK masks input values and leaves console and network capture off, so
 * what a visitor types never leaves the browser. That is not the same as their
 * data never leaving it: masking follows the element, not the value, so an
 * email the funnel types back out as ordinary text — the sign-up confirmation
 * screen, the account page's "log in with this address" step — is serialized
 * like any other text node once the visitor moves on from the input.
 *
 * Anything rendering a visitor's own data therefore carries `data-csr-mask`,
 * which the SDK masks by default. Put it on the smallest element that wraps
 * the value, since it masks the element's whole subtree and a replay is worth
 * more with the surrounding labels intact.
 *
 * Console capture stays off for the same reason it was turned off once before.
 * The Stripe client secret is a live credential — with the publishable key,
 * which ships in this bundle, it can read or confirm the payment — and it
 * reaches a log as a field of a serialized Stripe error when an intent
 * retrieval fails. This package already strips that secret from the URLs it
 * sends to Meta, in `getSanitizedEventSourceUrl`.
 */
export function initConfidenceSessionRecording(
  clientSecret: string | undefined,
): void {
  if (initialized) return

  if (!clientSecret) return

  // The build's prerender step and the screenshot tooling both load the real
  // production bundle in headless Chromium, which would otherwise file a
  // recording of a robot clicking through the funnel on every deploy.
  if (navigator.webdriver) return

  initialized = true

  const visitorId = resolveConfidenceVisitorId()

  // Imported lazily, and deliberately so: the recorder is 44 kB gzipped, which
  // is a sixth of the funnel's entry chunk, and none of it is needed to render
  // a page. Keeping it out of that chunk costs a recording that starts a moment
  // after the first paint instead of at it — the SDK snapshots the DOM when it
  // starts, so nothing before that point is lost, only unobserved.
  void import('@spotify-confidence/session-recording').then(
    ({ initSessionRecorder }) => {
      initSessionRecorder({
        clientSecret,
        context: visitorId ? { visitor_id: visitorId } : {},
        // Every field in the funnel is a controlled input, so React writes the
        // value back on each render and the recorder sees that write as an
        // input event of its own. This tags each one with whether a person was
        // behind it, which is the difference between an event list you can read
        // and one where every keystroke appears twice.
        userTriggeredOnInput: true,
        // A failed request is usually the reason a replay is worth watching,
        // but the checkout's requests are Stripe.js's and it retrieves an
        // intent with the client secret in the query string. `sanitize: true`
        // drops the query and fragment from every captured URL, which leaves
        // the method, path and status — all of what makes the capture useful,
        // none of the credential.
        //
        // Console output stays off. Its sanitizer only rewrites URLs found in
        // the text, and the way that secret reaches a log here is as a field of
        // a serialized Stripe error, which no URL rule touches.
        captureNetworkRequests: { sanitize: true },
      })
    },
  )
}
